import { EngineQueryResult } from "types/engineOutput.types"
import { RunScript } from "./engineRunner"

/**
 * A script that was not sent because its session was reset, its queue was
 * replaced, or its dashboard closed. Nothing to report: the tile is re-run
 * on the current queue, or no longer shown.
 */
export class SupersededRunError extends Error {
  constructor() {
    super("The run was superseded by a newer session.")
    this.name = "SupersededRunError"
  }
}

/**
 * One engine instance runs one script at a time, so runs are chained.
 * Identical script text shares one in-flight promise and, once done, one
 * cached result for the life of the session.
 *
 * Every call on the instance goes through its queue, scripts and other
 * requests alike: the engine does not serialise the calls of one instance,
 * and two at once mix their answers or stop it.
 */
export class QueryQueue {
  private tail: Promise<unknown> = Promise.resolve()
  private inflight = new Map<string, Promise<EngineQueryResult>>()
  private results = new Map<string, EngineQueryResult>()
  private disposed = false
  /** Moves on with each `clear`: a run started before it does not fill the cache after it. */
  private generation = 0

  /**
   * `isLive`: whether the session still takes calls (its own generation,
   * ready). The runner checks it again for a script; a request given to
   * `runExclusive` has only this check.
   */
  constructor(
    private readonly run: RunScript,
    private readonly isLive: () => boolean = () => true
  ) {}

  /** Runs `script` unless the queue was disposed while it waited its turn. */
  private runIfLive(script: string): Promise<EngineQueryResult> {
    if (this.disposed || !this.isLive()) return Promise.reject(new SupersededRunError())
    return this.run(script)
  }

  get size(): number {
    return this.inflight.size
  }

  cached(script: string): EngineQueryResult | undefined {
    return this.results.get(script)
  }

  /**
   * Caches `result` for `script` as though it had run, unless the script
   * already has a result or the queue was disposed. For a script that would
   * give the same result as one that just ran: the same tile without the
   * universe declaration the run has just made.
   */
  seed(script: string, result: EngineQueryResult): void {
    if (this.disposed || this.results.has(script)) return
    this.results.set(script, result)
  }

  enqueue(script: string): Promise<EngineQueryResult> {
    if (this.disposed) return Promise.reject(new SupersededRunError())
    const hit = this.results.get(script)
    if (hit) return Promise.resolve(hit)
    const pending = this.inflight.get(script)
    if (pending) return pending
    const generation = this.generation
    const job: Promise<EngineQueryResult> = this.tail
      .then(() => this.runIfLive(script))
      .then((result) => {
        if (generation === this.generation) this.results.set(script, result)
        return result
      })
      .finally(() => {
        if (this.inflight.get(script) === job) this.inflight.delete(script)
      })
    this.inflight.set(script, job)
    // A failed run must not stop the scripts queued behind it.
    this.tail = job.catch(() => undefined)
    return job
  }

  /**
   * Runs `script` in turn with the queued scripts but outside the cache and
   * the dedupe, for housekeeping (such as a `REMOVE`) that must really run
   * every time it is asked for.
   */
  runPlain(script: string): Promise<EngineQueryResult> {
    const job = this.tail.then(() => this.runIfLive(script))
    this.tail = job.catch(() => undefined)
    return job
  }

  /**
   * Runs `task`, a request on the session other than a script (such as a
   * read of its lineage), in turn with the queued scripts: after every
   * script queued before it has finished, async ones until their result is
   * fetched, and before any queued after it. An arrow, so it can be handed
   * on as it is.
   */
  runExclusive = <T>(task: () => Promise<T>): Promise<T> => {
    const job = this.tail.then(() =>
      this.disposed || !this.isLive() ? Promise.reject(new SupersededRunError()) : task()
    )
    this.tail = job.catch(() => undefined)
    return job
  }

  /**
   * Forgets every cached result and every script in flight: a script asked
   * for after this runs again, and one in flight now does not fill the
   * cache when it ends. For a session whose data changed under the cache.
   */
  clear(): void {
    this.generation++
    this.results.clear()
    this.inflight.clear()
  }

  /**
   * Stops the queue: the script already running finishes, every script
   * still waiting and every later one rejects with `SupersededRunError`.
   */
  dispose(): void {
    this.disposed = true
    this.results.clear()
  }
}
